import { Text, Box, InputGroup, Input, VStack } from '@chakra-ui/react';
import { SearchIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { DefaultCell } from 'components/common/Table/Cells';
import { Pagination } from 'components/common/Table/Pagination';
import { Table } from 'components/common/Table/Table';
import { ParticipantData } from 'components/common/Table/types/Column';
import { ClassMember, useSearchClassStudents } from 'hooks/api/backend/managament/getClasses';
import { useModalService } from 'hooks/useModalService';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalVariant } from 'types/Modal';
import { createColumns } from 'utils/tableHelper';

export type Props = {
  classId: string;
  students: ClassMember[];
};

export const ClassParticipantsContent = ({ classId, students }: Props): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const modalService = useModalService();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.length >= 1 ? searchQuery : '');
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: searchData, isLoading: isLoadingSearch } = useSearchClassStudents({
    classId: classId || '',
    searchQuery: debouncedSearchQuery,
    page: currentPage,
    enabled: !!classId,
  });

  const participantColumns = createColumns<ParticipantData>()({
    firstName: {
      header: 'Vorname',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
    },
    lastName: {
      header: 'Nachname',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
    },
    email: {
      header: 'E-Mail',
      sortDisabled: true,
    },
  });

  const mappedParticipants = useMemo(() => {
    const participants = debouncedSearchQuery && searchData ? searchData.students : students || [];

    return participants.map((participant) => {
      return {
        id: participant.id,
        firstName: participant.firstName,
        lastName: participant.lastName,
        email: participant.email,
      };
    });
  }, [students, debouncedSearchQuery, searchData]);

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const handleParticipantClick = (id: string): void => {
    modalService.show(ModalVariant.USER_WORKSHEETS, {
      classId,
      studentId: id,
    });
  };

  return (
    <VStack align="flex-start" gap="30px">
      {/* Participants Header */}
      <VStack align="flex-start" gap="l" width="100%">
        <Text textStyle="body.m.medium" color="text.secondary">
          Teilnehmende ({students.length})
        </Text>

        {/* Search Input */}
        <InputGroup
          startElement={<NovaIcon icon={SearchIcon} size="m" color="icon.accent" aria-hidden />}
        >
          <Input
            ref={inputRef}
            placeholder="Nachname, Vorname, E-Mail suchen"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="background.canvas"
            border="default"
            borderColor="border.accent"
            borderRadius="10px"
            h="44px"
            _placeholder={{ color: 'text.quaternary' }}
            _focus={{
              borderColor: 'border.accent.active',
              boxShadow: 'none',
            }}
          />
        </InputGroup>
      </VStack>

      {/* Participants Table */}
      <Box width="100%">
        <Table<ParticipantData>
          columns={participantColumns}
          data={mappedParticipants}
          isDataLoading={isLoadingSearch}
          onRowClick={handleParticipantClick}
          tableRowProps={{
            cursor: 'pointer',
            _hover: {
              bg: 'background.canvas',
            },
          }}
        />
        {searchData && searchData.totalPages > 1 && (
          <Pagination
            totalPages={searchData.totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Box>
    </VStack>
  );
};
